import { Control } from '@peckadesign/pd-naja/dist/types'
import Collapsable, { deepMerge } from 'collapsable.js'
import { CollapsableEvent, CollapsableOptions } from 'collapsable.js/dist/Collapsable'
import { HTMLCollapsableItem } from 'collapsable.js/dist/CollapsableItem'
import { DeepPartial } from 'collapsable.js/dist/utils'
import { CollapsableExtLink } from 'collapsable.js/dist/CollapsableExtLink'

type CollapsableGroup = {
	groupByParent?: string
	selector: string
	offTapClose: boolean
	options?: DeepPartial<CollapsableOptions>
}

type CollapsableStorageExpandedIds = Map<string, Set<string>>

class CollapsableControl implements Control {
	private readonly storageKey = 'expandedCollapsableIds'
	private expandedCollapsableIds: CollapsableStorageExpandedIds

	private offTapClose: HTMLCollapsableItem[] = []
	private commonOptions: DeepPartial<CollapsableOptions> = {
		externalLinks: {
			preventDefault: true
		}
	}
	private groups: CollapsableGroup[] = [
		{
			selector:
				'.js-collapsable:not(' +
				'	.js-collapsable-group .js-collapsable, ' +
				'	.js-collapsable--tabs, ' +
				'	.js-collapsable--off-tap-close, ' +
				'	[data-collapsable-overlay]' +
				')',
			offTapClose: false
		},
		{
			groupByParent: '.js-collapsable-group',
			selector: '.js-collapsable',
			offTapClose: false
		},
		{
			// Grouping by parent keeps each set of tabs in its own Collapsable, otherwise switching a tab in one set
			// would collapse the expanded tab in the others.
			groupByParent: '.js-collapsable-tabs',
			selector: '.js-collapsable--tabs',
			offTapClose: false,
			options: {
				accordion: true,
				collapsableAll: false
			}
		},
		{
			selector: '.js-collapsable--off-tap-close, [data-collapsable-overlay]',
			offTapClose: true
		}
	]

	public constructor() {
		this.attachHandlers()

		this.expandedCollapsableIds = this.parseExpandedIdsFromStorage()
	}

	public initialize(context: Element | Document): void {
		this.groups.forEach((group) => {
			if (group.groupByParent === undefined) {
				this.initializeGroup(context, group)
			} else {
				const groups = context.querySelectorAll<HTMLElement>(group.groupByParent)
				groups.forEach((groupElement) => this.initializeGroup(groupElement, group))
			}
		})

		this.restoreStateFromStorage(context)
	}

	private initializeGroup(context: Element | Document, group: CollapsableGroup): void {
		const collapsableElements = context.querySelectorAll<HTMLElement>(group.selector)

		if (collapsableElements.length === 0) {
			return
		}

		const options: DeepPartial<CollapsableOptions> = deepMerge({}, group.options, this.commonOptions)

		// If off tapping should close, we need to initialize items as separate Collapsable, otherwise
		// when `collapsableElements.length > 1`, off tap would close all items regardless of clicked target.
		if (group.offTapClose) {
			collapsableElements.forEach((element) => {
				const collapsable = new Collapsable(element, options)

				this.initializeElement(element, collapsable)
				this.offTapClose.push(element as HTMLCollapsableItem)
			})
		} else {
			const collapsable = new Collapsable(collapsableElements, options)
			const expandAllButton = group.groupByParent
				? context.querySelector<HTMLButtonElement>('[data-collapsable-expand-all]')
				: null
			const collapseAllButton = group.groupByParent
				? context.querySelector<HTMLButtonElement>('[data-collapsable-collapse-all]')
				: null

			collapsableElements.forEach((element) => {
				this.initializeElement(element, collapsable)
			})

			if (expandAllButton || collapseAllButton) {
				context.addEventListener(
					'expanded.collapsable',
					this.toggleHandleAllButtons.bind(this, collapsable, expandAllButton, collapseAllButton)
				)
				context.addEventListener(
					'collapsed.collapsable',
					this.toggleHandleAllButtons.bind(this, collapsable, expandAllButton, collapseAllButton)
				)

				expandAllButton?.addEventListener('click', () => collapsable.expandAll())
				collapseAllButton?.addEventListener('click', () => collapsable.collapseAll())
			}
		}
	}

	private initializeElement(element: HTMLElement, collapsable: Collapsable): void {
		element.querySelectorAll(`button.${collapsable.options.classNames.interactiveElement} a`).forEach((anchor) => {
			anchor.addEventListener('click', (event) => event.stopPropagation())
		})
	}

	private attachHandlers(): void {
		// Overlay
		document.body.addEventListener('expand.collapsable', this.handleOverlayClass.bind(this))
		document.body.addEventListener('collapse.collapsable', this.handleOverlayClass.bind(this))

		// Save state
		document.body.addEventListener('expand.collapsable', this.saveStateToStorage.bind(this))
		document.body.addEventListener('collapse.collapsable', this.saveStateToStorage.bind(this))

		// Prevent collapse
		document.body.addEventListener('collapse.collapsable', this.checkCollapsability.bind(this))

		// Off tap close
		document.body.addEventListener('click', this.handleOffTapClose.bind(this))
	}

	private handleOverlayClass(event: CollapsableEvent): void {
		const overlayClass = event.target && (event.target as HTMLElement).dataset.collapsableOverlay

		if (overlayClass) {
			const action = event.type === 'expand.collapsable' ? 'add' : 'remove'

			document.body.classList[action](overlayClass)
		}
	}

	private checkCollapsability(event: CollapsableEvent): void {
		if (event.target && (event.target as HTMLElement).classList.contains('js-collapsable--cannot-collapse')) {
			event.preventDefault()
		}
	}

	private handleOffTapClose(event: Event): void {
		if (!event.target) {
			return
		}

		const target = event.target as HTMLElement

		this.offTapClose.forEach((element: HTMLCollapsableItem) => {
			const hash = target.getAttribute('href')?.substring(1)
			const extLinks: CollapsableExtLink[] = element.collapsableItem.collapsable
				.getExtLinkById(hash)
				.filter((extLink: CollapsableExtLink) => extLink.extLink === event.target)

			if (element.contains(event.target as Node) || extLinks.length) {
				return
			}

			element.collapsableItem.collapsable.collapseAll()
		})
	}

	private saveStateToStorage(event: CollapsableEvent): void {
		const element = event.target as HTMLElement

		if (
			event.detail.collapsableEvent?.type === 'init.collapsable' ||
			event.detail.data?.skipStateSave ||
			element.dataset.collapsableSaveState === undefined
		) {
			return
		}

		const expandedForCurrentPathname = this.expandedCollapsableIds.get(location.pathname) || new Set()

		if (event.type === 'expand.collapsable') {
			expandedForCurrentPathname.add(element.id)
		} else {
			expandedForCurrentPathname.delete(element.id)
		}

		this.expandedCollapsableIds.set(location.pathname, expandedForCurrentPathname)
		localStorage.setItem(this.storageKey, JSON.stringify(this.expandedCollapsableIds, this.mapReplacer))
	}

	private restoreStateFromStorage(context: Element | Document): void {
		const expandedForCurrentPathname = this.expandedCollapsableIds.get(location.pathname) || []

		expandedForCurrentPathname.forEach((id) => {
			const element = context.querySelector(`#${id}`)

			if (!element || !('collapsableItem' in element)) {
				return
			}

			;(element as HTMLCollapsableItem).collapsableItem.expand(null, { skipStateSave: true }, true)
		})
	}

	private mapReplacer(key: any, value: any) {
		if (value instanceof Set || value instanceof Map) {
			return [...value]
		}

		return value
	}

	private parseExpandedIdsFromStorage(): CollapsableStorageExpandedIds {
		const expanded = new Map<string, Set<string>>(JSON.parse(localStorage.getItem(this.storageKey) || '[]'))

		expanded.forEach((value, key) => {
			expanded.set(key, new Set(value))
		})

		return expanded
	}

	private toggleHandleAllButtons(
		collapsable: Collapsable,
		expandAllButton: HTMLButtonElement | null,
		collapseAllButton: HTMLButtonElement | null
	): void {
		const expanded = collapsable.getExpanded()

		if (expandAllButton) {
			expandAllButton.disabled = expanded.length === collapsable.items.length
		}

		if (collapseAllButton) {
			collapseAllButton.disabled = expanded.length === 0
		}
	}
}

export default new CollapsableControl()
